import { useRef } from 'react';
import { Formik, FormikProps } from 'formik';
import { useRouter } from '@uirouter/react';

import { notifySuccess } from '@/portainer/services/notifications';
import { useAnalytics } from '@/react/hooks/useAnalytics';
import { useCanExit } from '@/react/hooks/useCanExit';
import { useEnvironmentId } from '@/react/hooks/useEnvironmentId';

import { confirmGenericDiscard } from '@@/modals/confirm';

import { Chart, ChartReference } from '../types';
import { useUpdateHelmReleaseMutation } from '../helmReleaseQueries/useUpdateHelmReleaseMutation';
import { RegistryValue } from '../components/HelmRegistrySelect';
import { useHelmRepoVersions } from '../helmChartSourceQueries/useHelmRepoVersions';

import { HelmInstallInnerForm } from './HelmInstallInnerForm';
import { HelmInstallFormValues } from './types';

type Props = {
  selectedChart: Chart;
  namespace?: string;
  name?: string;
  isRegistryAvailable: boolean;
  selectedRegistry: RegistryValue | null;
};

export function HelmInstallForm({
  selectedChart,
  namespace,
  name,
  isRegistryAvailable,
  selectedRegistry,
}: Props) {
  const environmentId = useEnvironmentId();
  const router = useRouter();
  const analytics = useAnalytics();
  const versionOptionsQuery = useHelmVersionOptions({
    chartPath: selectedChart.name,
    repoURL: selectedRegistry?.registryId ? undefined : selectedChart.repo,
    registryID: selectedRegistry?.registryId,
  });

  const defaultVersion = versionOptionsQuery.data?.[0]?.value;
  const initialValues: HelmInstallFormValues = {
    values: '',
    version: defaultVersion?.version ?? '',
    repo: defaultVersion?.repo ?? selectedChart.repo ?? '',
    registryId: selectedRegistry?.registryId,
  };

  const installHelmChartMutation = useUpdateHelmReleaseMutation(environmentId);

  const formikRef = useRef<FormikProps<HelmInstallFormValues>>(null);
  useCanExit(() => !formikRef.current?.dirty || confirmGenericDiscard());

  return (
    <Formik
      innerRef={formikRef}
      initialValues={initialValues}
      enableReinitialize
      onSubmit={handleSubmit}
    >
      <HelmInstallInnerForm
        selectedChart={selectedChart}
        namespace={namespace}
        name={name}
        versionOptions={versionOptionsQuery.data ?? []}
        isVersionsLoading={versionOptionsQuery.isInitialLoading}
        isRegistryAvailable={isRegistryAvailable}
        selectedRegistry={selectedRegistry}
      />
    </Formik>
  );

  async function handleSubmit(values: HelmInstallFormValues) {
    if (!name || !namespace) {
      // Theoretically this should never happen and is mainly to keep typescript happy
      return;
    }

    await installHelmChartMutation.mutateAsync(
      {
        name,
        repo: selectedRegistry?.registryId ? undefined : selectedChart.repo,
        registryId: selectedRegistry?.registryId,
        chart: selectedChart.name,
        values: values.values,
        namespace,
        version: values.version,
      },
      {
        onSuccess() {
          analytics.trackEvent('kubernetes-helm-install', {
            category: 'kubernetes',
            metadata: {
              'chart-name': selectedChart.name,
            },
          });
          notifySuccess('Success', 'Helm chart successfully installed');

          // Reset the form so page can be navigated away from without getting "Are you sure?"
          formikRef.current?.resetForm();
          router.stateService.go('kubernetes.applications');
        },
      }
    );
  }
}

function useHelmVersionOptions(chartReference: ChartReference) {
  return useHelmRepoVersions(
    {
      staleTime: 60 * 60 * 1000, // 1 hour cache
      select: (versions) =>
        versions.map((version, index) => ({
          label: index === 0 ? `${version.version} (latest)` : version.version,
          value: version,
        })),
    },
    chartReference
  );
}
